import { useState, useEffect, useCallback } from "react"
import PubSub from "pubsub-js"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { ListThemes } from "../FileShareFeature/api"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import ThemeThumbnail from "./ThemeCard"
import {
  VIRTUAL_DEFAULT_THEME, DEFAULT_THEME_ID,
  VIRTUAL_ACCOUNT_THEME, ACCOUNT_DEFAULT_ID,
} from "./constants"

// Mobile-parity theme picker — port of iOS `ThemeSelectField`.
//
// A flat, scrollable list of rows (Account Default → System Default → owned
// themes). One tap = pick. No dropdown, no search, no "+ Create new theme"
// affordance. Used in the share drawer's Theme tab where the user is past
// the upload flow and just wants to restamp the theme.
//
// Pairs with the same realtime + ListThemes plumbing as ThemeSelect.
export default function ThemeList({ value, onChange }) {
  const [themes, setThemes] = useState([])

  const texts = TextBundle({ id: "transfer_theme_editor" })

  const load = useCallback(async () => {
    const res = await ListThemes().catch(() => null)
    setThemes(res?.themes || [])
  }, [])

  useEffect(() => { load() }, [load])

  // Realtime: refetch when themes change elsewhere (create/edit/delete/default).
  useEffect(() => {
    const token = PubSub.subscribe("transfer.theme.changed", () => load())
    return () => PubSub.unsubscribe(token)
  }, [load])

  // Account Default's preview inherits whichever theme is currently flagged
  // is_default, so the row shows the look it'll actually produce — same as
  // ThemeSelect + iOS ThemeSelectField.
  const accountSource = themes.find((t) => t.content?.is_default)
  const sourceName = accountSource?.content?.name || VIRTUAL_DEFAULT_THEME.content.name
  const accountOption = {
    ...VIRTUAL_ACCOUNT_THEME,
    content: {
      ...(accountSource?.content || VIRTUAL_DEFAULT_THEME.content),
      name: `${VIRTUAL_ACCOUNT_THEME.content.name} · ${sourceName}`,
    },
  }

  // Account Default is the implicit fallback (matches ThemeSelect): an unset
  // / unknown value reads as the account row.
  const selectedId = (() => {
    if (value === DEFAULT_THEME_ID) return DEFAULT_THEME_ID
    if (value === ACCOUNT_DEFAULT_ID) return ACCOUNT_DEFAULT_ID
    if (themes.some((t) => t._id === value)) return value
    return ACCOUNT_DEFAULT_ID
  })()

  const rows = [
    accountOption,
    VIRTUAL_DEFAULT_THEME,
    ...themes,
  ]

  return (
    <div className="flex flex-col divide-y divide-white/5">
      {rows.map((opt) => {
        const id = opt._id
        const selected = id === selectedId
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange?.(id)}
            className={`flex items-center gap-3 px-2 py-2.5 text-left transition-colors hover:bg-neutral ${
              selected ? "bg-primary/10" : ""
            }`}
          >
            <div className="w-14 shrink-0">
              <ThemeThumbnail theme={opt} />
            </div>
            <span className={`flex-1 text-sm truncate ${selected ? "text-primary font-medium" : "text-text"}`}>
              {opt.content?.name || texts?.untitled}
            </span>
            {selected && (
              <CheckCircleIcon className="h-5 w-5 text-primary shrink-0" />
            )}
          </button>
        )
      })}
    </div>
  )
}
